import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  validateStockOpnameCompletion,
  type OpnameItemErrors,
} from "./stock-opname-schema";
import {
  getApplicableReasons,
  isOpnameLineCounted,
} from "./stock-opname-utils";

import type {
  StockOpname,
  StockOpnameItem,
  StockReason,
  UpdateStockOpnameItemInput,
} from "@artisancode/api-types";

interface UseStockOpnameFormProps {
  opname: StockOpname;
  reasons?: StockReason[];
  onSaveDraft: (data: {
    note?: string | null;
    items?: UpdateStockOpnameItemInput[];
  }) => Promise<void>;
  onComplete: (data: { items?: UpdateStockOpnameItemInput[] }) => Promise<void>;
  onClose: () => void;
  readOnly?: boolean;
}

export function useStockOpnameForm({
  opname,
  reasons = [],
  onSaveDraft,
  onComplete,
  onClose,
  readOnly = false,
}: UseStockOpnameFormProps) {
  const [items, setItems] = useState<StockOpnameItem[]>(
    () => opname.items?.map((i) => ({ ...i })) ?? [],
  );
  const [notes, setNotes] = useState(opname.note || "");
  const [query, setQuery] = useState("");
  const [onlyDiscrepancies, setOnlyDiscrepancies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<OpnameItemErrors>({});
  const isRawMaterial = opname.type === "raw_material";

  // Lines are added/removed on the server; merge those in but keep counts typed and not yet saved.
  const [serverItems, setServerItems] = useState(opname.items);
  if (opname.items !== serverItems) {
    setServerItems(opname.items);
    setItems((prev) => {
      const local = new Map(prev.map((i) => [i.id, i]));
      return (opname.items ?? []).map((i) => local.get(i.id) ?? { ...i });
    });
  }

  function clearError(itemId: string) {
    setErrors((prev) => {
      if (!prev[itemId]) return prev;
      return Object.fromEntries(
        Object.entries(prev).filter(([id]) => id !== itemId),
      );
    });
  }

  function handleOnHandChange(itemId: string, val: string) {
    clearError(itemId);
    const countedOnHand = val === "" ? null : Number(val);
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const diffOnHand =
          countedOnHand !== null ? countedOnHand - item.systemOnHand : 0;
        let reasonId = item.reasonId;
        if (reasonId && reasons.length > 0) {
          const applicable = getApplicableReasons(
            { diffOnHand, diffRepacking: item.diffRepacking },
            reasons,
          );
          if (!applicable.some((r) => r.id === reasonId)) {
            reasonId = null;
          }
        }
        return {
          ...item,
          countedOnHand,
          diffOnHand,
          reasonId,
        };
      }),
    );
  }

  function handleRepackingChange(itemId: string, val: string) {
    clearError(itemId);
    const countedRepacking = val === "" ? null : Number(val);
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const diffRepacking =
          countedRepacking !== null
            ? countedRepacking - item.systemRepacking
            : 0;
        let reasonId = item.reasonId;
        if (reasonId && reasons.length > 0) {
          const applicable = getApplicableReasons(
            { diffOnHand: item.diffOnHand, diffRepacking },
            reasons,
          );
          if (!applicable.some((r) => r.id === reasonId)) {
            reasonId = null;
          }
        }
        return {
          ...item,
          countedRepacking,
          diffRepacking,
          reasonId,
        };
      }),
    );
  }

  function handleReasonChange(itemId: string, reasonId: string) {
    clearError(itemId);
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, reasonId } : item)),
    );
  }

  function handleNoteChange(itemId: string, note: string) {
    clearError(itemId);
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, note } : item)),
    );
  }

  // Calculate live statistics
  const totalItems = items.length;
  const discrepancyCount = items.filter(
    (i) => i.diffOnHand !== 0 || i.diffRepacking !== 0,
  ).length;
  // Uncounted lines are neither a match nor a discrepancy yet.
  const matchCount = items.filter(
    (i) =>
      isOpnameLineCounted(i, isRawMaterial) &&
      i.diffOnHand === 0 &&
      i.diffRepacking === 0,
  ).length;
  const minusCount = items.filter(
    (i) => i.diffOnHand + i.diffRepacking < 0,
  ).length;
  const plusCount = items.filter(
    (i) => i.diffOnHand + i.diffRepacking > 0,
  ).length;

  const netDiffValue = useMemo(() => {
    return items.reduce(
      (sum, i) => sum + (i.diffOnHand + i.diffRepacking) * (i.costPerUnit || 0),
      0,
    );
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((i) => {
      const q = query.toLowerCase();
      const matchesQuery =
        !q ||
        i.itemName.toLowerCase().includes(q) ||
        (i.itemSku && i.itemSku.toLowerCase().includes(q)) ||
        (i.categoryName && i.categoryName.toLowerCase().includes(q));
      if (onlyDiscrepancies) {
        return matchesQuery && (i.diffOnHand !== 0 || i.diffRepacking !== 0);
      }
      return matchesQuery;
    });
  }, [items, query, onlyDiscrepancies]);

  function toItemInputs(): UpdateStockOpnameItemInput[] {
    return items.map((i) => ({
      id: i.id,
      countedOnHand: i.countedOnHand,
      countedRepacking: i.countedRepacking,
      reasonId: i.reasonId,
      note: i.note,
    }));
  }

  async function handleSaveDraft() {
    try {
      setIsSubmitting(true);
      await onSaveDraft({
        note: notes,
        items: toItemInputs(),
      });
      toast.success("Draft Stock Opname berhasil disimpan.");
      onClose();
    } catch {
      // toast shown by react query mutation cache
    } finally {
      setIsSubmitting(false);
    }
  }

  /** Marks every invalid line and toasts a summary; true when the session can be completed. */
  function validateCompletion(): boolean {
    const result = validateStockOpnameCompletion(items, isRawMaterial, reasons);
    setErrors(result.errors);
    if (!result.summary) return true;
    toast.error(result.summary);
    // The first flagged field may sit under the sticky action bar; bring it into view.
    requestAnimationFrame(() =>
      document
        .querySelector('[aria-invalid="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
    return false;
  }

  async function handleComplete() {
    if (!validateCompletion()) return;

    try {
      setIsSubmitting(true);
      await onComplete({
        items: toItemInputs(),
      });
      toast.success("Stock Opname selesai! Mutasi kartu stok telah diposting.");
      onClose();
    } catch {
      // toast shown by react query
    } finally {
      setIsSubmitting(false);
    }
  }

  const isCompleted = opname.status === "completed";
  const isCancelled = opname.status === "cancelled";
  const isLocked = readOnly || isCompleted || isCancelled;

  return {
    notes,
    setNotes,
    query,
    setQuery,
    onlyDiscrepancies,
    setOnlyDiscrepancies,
    filteredItems,
    handleOnHandChange,
    handleRepackingChange,
    handleReasonChange,
    handleNoteChange,
    handleSaveDraft,
    handleComplete,
    isSubmitting,
    totalItems,
    matchCount,
    minusCount,
    plusCount,
    discrepancyCount,
    netDiffValue,
    errors,
    validateCompletion,
    isCompleted,
    isCancelled,
    isLocked,
  };
}
